import {
  CallState,
  DurationRefinement,
  ExperienceAvailability,
  ExperienceId,
  ExperiencesAvailabilityAutocompleteResponse,
  ExperiencesAvailabilityAutocompleteResult,
  ExperiencesAvailabilityRequestEnum,
  ExperiencesAvailabilityResponse,
  ExperiencesAvailabilitySort,
  ExperienceTag,
  ExperienceTagCount,
  ICategorizedResponse,
  PriceRefinement,
  Prices,
  SearchPageToken,
  StartTimeRefinement,
  TripadvisorRatingRefinement,
} from "redmond";
import { actions, actionTypes } from "../actions";

export enum ExperiencesAvailabilityCallState {
  NotCalled = "NotCalled",
  InitialSearchCallInProcess = "InitialSearchCallInProcess",
  InitialFilteredSearchCallInProcess = "InitialFilteredSearchCallInProcess",
  FollowUpSearchCallInProcess = "FollowUpSearchCallInProcess",
  InitialSearchCallSuccess = "InitialSearchCallSuccess",
  FollowUpSearchCallSuccess = "FollowUpSearchCallSuccess",
  // when a FollowUpSearch returns empty
  Complete = "Complete",
  Failed = "Failed",
}

export interface IExperiencesAvailabilityFilterState {
  tripAdvisorRating?: TripadvisorRatingRefinement;
  duration?: DurationRefinement[];
  startTime?: StartTimeRefinement[];
  price?: PriceRefinement;
  tags?: ExperienceTag[];
  popularFeatures?: ExperienceTag[];
  sort: ExperiencesAvailabilitySort;
}

export interface IExperiencesAvailabilityState
  extends IExperiencesAvailabilityFilterState {
  availabilityResponse?: ExperiencesAvailabilityResponse;
  // all experiences
  availabilityExperiences: ExperienceAvailability[];
  // map of experience id to experience object
  experiencesMap: Map<string, ExperienceAvailability>;
  // map of experiences to the category they belong in
  experiencesByCategoryMap: { [id: number]: ExperienceId[] };
  categoryTags: ExperienceTagCount[];
  availabilityNextPageToken?: SearchPageToken;
  experiencesAvailabilityCallState: ExperiencesAvailabilityCallState;
  keyword: ExperiencesAvailabilityAutocompleteResult | null;
  keywordCategories: ICategorizedResponse[];
  filtersChangedSinceLastSearch: boolean;
  priceFiltersChangedSinceLastSearch: boolean;
  autocompleteCallState: CallState;
  autocompleteResults?: ExperiencesAvailabilityAutocompleteResponse;
  isResetting: boolean;
  availabilityMaxPrice?: Prices;
  availabilityMinPrice?: Prices;
  selectedGroupedExperience?: ExperienceTagCount;
}

export const initialState: IExperiencesAvailabilityState = {
  experiencesMap: new Map(),
  experiencesByCategoryMap: {},
  categoryTags: [],
  availabilityResponse: undefined,
  availabilityExperiences: [],
  experiencesAvailabilityCallState: ExperiencesAvailabilityCallState.NotCalled,
  keyword: null,
  keywordCategories: [],
  filtersChangedSinceLastSearch: false,
  priceFiltersChangedSinceLastSearch: false,
  sort: ExperiencesAvailabilitySort.Recommended,
  autocompleteCallState: CallState.NotCalled,
  isResetting: false,
  selectedGroupedExperience: undefined,
};

export const reducer = (
  state: IExperiencesAvailabilityState = initialState,
  action: actions.ExperiencesAvailabilityActions
): IExperiencesAvailabilityState => {
  switch (action.type) {
    case actionTypes.FETCH_INITIAL_EXPERIENCES_AVAILABILITY:
      return {
        ...state,
        experiencesAvailabilityCallState:
          ExperiencesAvailabilityCallState.InitialSearchCallInProcess,
        filtersChangedSinceLastSearch: false,
        isResetting: false,
        priceFiltersChangedSinceLastSearch: false,
      };
    case actionTypes.FETCH_FILTERED_EXPERIENCES_AVAILABILITY:
      return {
        ...state,
        experiencesAvailabilityCallState:
          ExperiencesAvailabilityCallState.InitialFilteredSearchCallInProcess,
        filtersChangedSinceLastSearch: false,
        isResetting: false,
        priceFiltersChangedSinceLastSearch: false,
      };
    case actionTypes.FETCH_MORE_EXPERIENCES_AVAILABILITY:
      return {
        ...state,
        experiencesAvailabilityCallState:
          ExperiencesAvailabilityCallState.FollowUpSearchCallInProcess,
        filtersChangedSinceLastSearch: false,
        priceFiltersChangedSinceLastSearch: false,
      };
    case actionTypes.SET_EXPERIENCES_AVAILABILITY_RESULTS: {
      if (state.isResetting) {
        return state;
      }

      const isInitialRequest =
        action.responseType ===
        ExperiencesAvailabilityRequestEnum.InitialSearch;
      const hasCompletedRequest =
        !action.payload.nextPageToken ||
        action.payload.experiences.length === 0;
      const resetAvailabilityResponse =
        action.resetAvailabilityResponse ?? true;
      const resetPriceFilterConstraints =
        action.resetPriceFilterConstraints ?? true;

      let newExperiencesMap = state.experiencesMap;
      let newExperiencesByCategoryMap = state.experiencesByCategoryMap;
      let newCategoryTags = state.categoryTags;
      let newAvailabilityResponse = state.availabilityResponse;
      let newAvailabilityExperiences = state.availabilityExperiences || [];
      let newCallState = ExperiencesAvailabilityCallState.NotCalled;
      let newAutocompleteResults = state.autocompleteResults;
      let newAutocompleteCallState = state.autocompleteCallState;
      let newMaxPrice = state.availabilityMaxPrice;
      let newMinPrice = state.availabilityMinPrice;
      let newSelectedGroupedExperience = state.selectedGroupedExperience;

      if (isInitialRequest) {
        newExperiencesMap = new Map();
        newExperiencesByCategoryMap = {};
        newAvailabilityExperiences = [];
        newAutocompleteResults = undefined;
        newAutocompleteCallState = CallState.NotCalled;
        newCategoryTags = action.payload.categoryTags;
      }

      if (resetAvailabilityResponse) {
        newAvailabilityResponse = action.payload;
      }

      if (resetPriceFilterConstraints) {
        newMaxPrice = action.payload.maxPrice;
        newMinPrice = action.payload.minPrice;
      }

      if (hasCompletedRequest) {
        newCallState = ExperiencesAvailabilityCallState.Complete;
      } else if (isInitialRequest) {
        newCallState =
          ExperiencesAvailabilityCallState.InitialSearchCallSuccess;
      } else {
        newCallState =
          ExperiencesAvailabilityCallState.FollowUpSearchCallSuccess;
      }

      action.payload.experiences.forEach((item) => {
        newExperiencesMap.set(item.id.value, item);
        const itemTags = item.tags;
        itemTags.forEach((tag) => {
          if (newExperiencesByCategoryMap[tag.id]) {
            newExperiencesByCategoryMap[tag.id].push(item.id);
          } else {
            newExperiencesByCategoryMap[tag.id] = [item.id];
          }
        });
      });

      if (
        action.request.AvailabilityRequest ===
          ExperiencesAvailabilityRequestEnum.InitialSearch &&
        action.request.refinement.tags.length > 0 &&
        action.payload.categoryTags
      ) {
        // Find the category tag with an empty name, which indicates that user has accessed the site via a url
        // that has categoryTagId assigned. i.e experience cross sell on confirmation page
        const categoryTagId = action.request.refinement.tags.find(
          (tag) => tag.name === ""
        )?.id;

        // If we found a category tag with an empty name, set it as the selected grouped experience
        if (categoryTagId) {
          newSelectedGroupedExperience = action.payload.categoryTags.find(
            (tag) => tag.tag.id === categoryTagId
          );
        }
      }

      return {
        ...state,
        availabilityResponse: newAvailabilityResponse,
        availabilityExperiences: [
          ...newAvailabilityExperiences,
          ...action.payload.experiences,
        ],
        experiencesMap: newExperiencesMap,
        experiencesByCategoryMap: newExperiencesByCategoryMap,
        categoryTags: newCategoryTags,
        availabilityNextPageToken: action.payload.nextPageToken,
        experiencesAvailabilityCallState: newCallState,
        autocompleteCallState: newAutocompleteCallState,
        autocompleteResults: newAutocompleteResults,
        availabilityMaxPrice: newMaxPrice,
        availabilityMinPrice: newMinPrice,
        selectedGroupedExperience: newSelectedGroupedExperience,
      };
    }

    case actionTypes.SET_EXPERIENCES_AVAILABILITY_CALL_STATE_FAILED:
      return {
        ...state,
        experiencesAvailabilityCallState:
          ExperiencesAvailabilityCallState.Failed,
      };

    case actionTypes.RESET_EXPERIENCES_AVAILABILITY:
      return {
        ...initialState,
        isResetting: true,
      };

    case actionTypes.SET_KEYWORD: {
      const { keyword } = action;
      return {
        ...state,
        keyword: keyword ? { ...keyword } : null,
      };
    }

    case actionTypes.SET_FILTER_TRIPADVISOR_RATING: {
      return {
        ...state,
        tripAdvisorRating: action.rating,
        filtersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.SET_FILTER_START_TIMES: {
      return {
        ...state,
        startTime: action.startTimes,
        filtersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.SET_FILTER_DURATION: {
      return {
        ...state,
        duration: action.durationTimes,
        filtersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.SET_FILTER_PRICE: {
      return {
        ...state,
        price: action.price,
        filtersChangedSinceLastSearch: false,
        priceFiltersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.SET_FILTER_TAGS: {
      return {
        ...state,
        tags: action.tags,
        filtersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.SET_FILTER_POPULAR_FEATURES: {
      return {
        ...state,
        popularFeatures: action.tags,
        filtersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.SET_SORT_OPTION: {
      return {
        ...state,
        sort: action.sortOption,
        filtersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.RESET_FILTER_AND_SORT: {
      return {
        ...state,
        tripAdvisorRating: TripadvisorRatingRefinement.Any,
        duration: [],
        startTime: [],
        price: undefined,
        tags: [],
        popularFeatures: [],
        sort: ExperiencesAvailabilitySort.Recommended,
        filtersChangedSinceLastSearch: true,
      };
    }

    case actionTypes.FETCH_EXPERIENCES_AVAILABILITY_AUTOCOMPLETE: {
      return {
        ...state,
        autocompleteCallState: CallState.InProcess,
      };
    }

    case actionTypes.SET_EXPERIENCES_AVAILABILITY_AUTOCOMPLETE_RESULTS: {
      return {
        ...state,
        autocompleteResults: action.autocompleteResults,
        autocompleteCallState: CallState.Success,
      };
    }

    case actionTypes.SET_EXPERIENCE_TAG_COUNT: {
      return {
        ...state,
        selectedGroupedExperience: action.tag,
      };
    }

    default:
      return { ...state };
  }
};

export * from "./selectors";
